import React, { lazy, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { endpoints } from "config";
import DeleteModal from "components/helpers/DeleteModal";

// Icons
import View from "assets/Icon_Action_01.png";
import Update from "assets/Icon_Action_02.png";
import Delete from "assets/Icon_Action_03.png";

import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { format } from "date-fns";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import { HeaderDocuComp } from "./helpers/HeaderDocuComp";
import { SelectOption } from "components/dropdown/Dropdown";
import { FilePicker } from "./helpers/FilePicker";
import {
  documentsDownloadSingleDoc,
  documentsFilesDownloader,
} from "./helpers/useDownloader";
import MarkAsFavouriteModal from "./helpers/MarkAsFavouriteModel";

import download_png from "assets/download.png";
import download_svg from "assets/download.svg";
import { CustomInitPage } from "./helpers/CustomInitPage";
import {
  NestedListItemProps,
  RolePermissions,
  TCustomCell,
} from "components/Category/Category";
import {
  CustomRowCellColumns,
  CustomRowCellData,
  resetRedirectUrl,
  resetRedirectUrlDestinationModule,
  selectCustomRowData,
  selectRedirectUrl,
  selectRedirectUrlDestinationModule,
  setParentId,
} from "reducers/listPage";
import { useDispatch, useSelector } from "react-redux";
import { match } from "ramda";
import { parseListDate } from "helpers/parseListDate";
import DocumentsDownloadPlaceholder from "./DocumentsDownloadPlaceholder";
import {
  clearSubmitResponseErrors,
  selectSubmitResponse,
} from "reducers/submitResponse";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";

const Document = lazyWithRetry(() => import("./Document"));

// Define which column on the table represents each row's ID.
// This should be unique to each row.
const idColumn = "documents__Id";

const rolesPermissions: RolePermissions<Data> = {
  ["bUpload"]: [
    "edit",
    "upload",
    "documents-upload",
    "documents-delete",
    "delete",
    "documents-edit",
  ],
};

//Define the data structure for table
export type Data = {
  documents__Id: number;
  documents__Name: string;
  documents__DocumentLevel: string | null;

  documents__DocumentFormat: string | null;
  documentLevels__DocumentLevelsName: string | null;
  documentTypes__DocumentTypesName: string | null;
  documents__Description: string | null;
  documents__bFavourite: boolean;
  documents__bSuppressed: boolean;
  documents__AddDate: string;
  documents__AddedBy: string;
  documents__EditDate: string;
  documents__EditedBy: string | null;
  documents__DocumentVersion: string | null;
  documents__ListDocumentVersions: SelectOption | null;
  documents__FilePath: string | null;
  bUpload: boolean;
};

const getCrudItems = (
  nestedListItemProps?: NestedListItemProps,
  parentSelectedId?: number,
  parentSelectedIdName?: string,
  lowerCaseParentSelectedIdName?: string
): CrudItems<Data> => {
  return [
    {
      icon: View,
      title: "Add new document" /* REPLACE__ME */,
      imgAltText: "Create",
      menuCode: "CREATE",
      showInMultiSelect: false,
      permissionsKey: parentSelectedId ? "documents-create" : "create",
      content: (onDone, selectedData, menu, additionalData) => (
        <Document
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="create"
          nestedListPageUrl={nestedListItemProps?.urls?.create ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
      notInMenus: true,
    },
    {
      icon: View,
      title: "View document info" /* REPLACE__ME */,
      imgAltText: "View",

      permissionsKey: parentSelectedId ? "documents-view" : "view",
      menuCode: "VIEW",
      showInMultiSelect: false,
      content: (
        onDone,
        selectedData,
        menu,
        additionalData,
        clickedIdIfMultiIds
      ) => {
        return (
          <Document
            onDone={onDone}
            selectedData={selectedData}
            idFromRowOption={menu.selectedId}
            actionType="view"
            nestedListPageUrl={nestedListItemProps?.urls?.view ?? undefined}
            parentSelectedId={parentSelectedId ?? undefined}
            additionalData={additionalData}
            autoMenuActionsCode={"VIEW"}
            clickedIdIfMultiIds={clickedIdIfMultiIds}
            noSubmit
          />
        );
      },
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: Update,
      imgAltText: "Update",

      permissionsKey: parentSelectedId ? "documents-edit" : "edit",
      title: "Update document info" /* REPLACE__ME */,
      menuCode: "UPDATE",
      showInMultiSelect: false,
      content: (onDone, selectedData, menu, additionalData) => {
        return (
          <Document
            selectedData={selectedData}
            onDone={onDone}
            idFromRowOption={menu.selectedId}
            actionType="edit"
            nestedListPageUrl={nestedListItemProps?.urls?.edit ?? undefined}
            parentSelectedId={parentSelectedId ?? undefined}
            additionalData={additionalData}
          />
        );
      },
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: View,
      imgAltText: "Upload",

      title: "Upload new version" /* REPLACE__ME */,
      menuCode: "UPLOAD",
      showInMultiSelect: true,
      permissionsKey: parentSelectedId ? "documents-upload" : "upload",
      content: (onDone, selectedData, menu, additionalData) => null,
      inRightPanel: false, //true, //false,
      isDisabled: false,
      doNotLoadContentInRightPanel: true,

      customItem: FilePicker,
      alternateMenuItemOnClick: (
        dispatch: React.Dispatch<any>,
        selectedDataIDs: number[] | null
      ) => {
        // console.log("UPLOAD NEW VERSION selectedDataIDs: ", selectedDataIDs);
      },
    },
    {
      icon: View,
      imgAltText: "Download",

      title: "Download" /* REPLACE__ME */,
      menuCode: "DOWNLOAD",
      permissionsKey: parentSelectedId ? "documents-download" : "download",
      showInMultiSelect: true,

      content: (onDone, selectedData, menu, additionalData) => (
        <DocumentsDownloadPlaceholder
          selectedData={selectedData}
          onDone={onDone}
          additionalData={additionalData}
          nestedListPageUrl={nestedListItemProps?.urls?.download ?? undefined}
          parentSelectedIdName={lowerCaseParentSelectedIdName}
          parentSelectedId={parentSelectedId ?? undefined}
          fileExtensionColumnName={
            parentSelectedId
              ? "documents__FileExtension"
              : "documents__DocumentFormat"
          }
        />
      ),
      inRightPanel: false,
      doNotLoadContentInRightPanel: true,
      isDisabled: false,
      // alternateMenuItemOnClick: documentsFilesDownloader,
    },
    {
      icon: Update,
      imgAltText: "Mark as Favourite",

      title: data =>
        data.every(d => !!d.documents__bFavourite)
          ? "Remove from favourites"
          : "Mark As Favourite",
      menuCode: "MARK_AS_FAVOURITE",
      showInMultiSelect: true,
      permissionsKey: parentSelectedId ? "documents-favourite" : "favourite",
      content: (onActionComplete, selectedData, menu, additionalData) => (
        <MarkAsFavouriteModal
          url={
            nestedListItemProps?.urls?.favourite ??
            endpoints.documents.markasfavourite /* REPLACE__ME */
          }
          noun="document"
          selectedIds={selectedData.map(
            d => d.documents__Id /** */
          )} /*
            REPLACE__ME - return id */
          renderListItem={data =>
            /** return the name to be displayed */ data.documents__Name
          }
          selectedData={selectedData}
          open={menu.currentMenu === "MARK_AS_FAVOURITE"}
          close={onActionComplete}
          idFromRowOption={menu.selectedId}
          setToFavourite={
            selectedData.every(d => !!d.documents__bFavourite) ? false : true
          }
          additionalDataIDs={additionalData}
          parentSelectedIdName={lowerCaseParentSelectedIdName ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: false,
      isDisabled: false,
    },
    {
      icon: Delete,
      imgAltText: "Delete",

      permissionsKey: parentSelectedId ? "documents-delete" : "delete",
      title: data =>
        data.length <= 1
          ? "Delete document"
          : `Delete ${data.length} documents `,
      menuCode: "DELETE",
      showInMultiSelect: true,
      content: (onActionComplete, selectedData, menu, additionalData) => (
        <DeleteModal
          url={nestedListItemProps?.urls?.delete ?? endpoints.documents.delete}
          noun="document"
          selectedIds={selectedData.map(
            d => d.documents__Id /** */
          )} /*
            REPLACE__ME - return id */
          renderListItem={data =>
            /** return the name to be displayed */ data.documents__Name
          }
          selectedData={selectedData}
          open={menu.currentMenu === "DELETE"}
          close={onActionComplete}
          idFromRowOption={menu.selectedId}
          additionalDataIDs={additionalData}
          parentSelectedIdName={lowerCaseParentSelectedIdName ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: false,
      isDisabled: false,
    },
  ];
};

// const crudItems: CrudItems<Data> = ;

// Optional - function for formatting a row by column name. Takes a row and should
// spit out an object with the same keys.
// Should do this for date columns and format them as required
const rowFormatter = (
  row: Data
): { [K in keyof typeof row]: React.ReactNode } => {
  let formattedRow: any = { ...row };

  if (formattedRow.documents__AddDate) {
    formattedRow = {
      ...formattedRow,
      documents__AddDate: parseListDate(formattedRow.documents__AddDate, true),
    };
  }
  if (formattedRow.documents__EditDate) {
    formattedRow = {
      ...formattedRow,
      documents__EditDate: parseListDate(
        formattedRow.documents__EditDate,
        true
      ),
    };
  }

  return formattedRow;
};

export interface DocumentsProps {
  parentSelectedId?: number;
  nestedListItemProps?: NestedListItemProps;
  parentSelectedIdName?: string;
  lowerCaseParentSelectedIdName?: string;
}

export type Props = DocumentsProps;

export const DOCUMENT_VERSION_COLUMN = "documents__DocumentVersion";

const Documents: React.FC<Props> = props => {
  const location = useLocation();

  let customRowCells: {
    [k in keyof Data]?: TCustomCell<Data>;
  } = {};

  const [finalRedirectUrl, setFinalRedirectUrl] = useState<string | null>(null);

  customRowCells[CustomRowCellColumns.DOCUMENTS_VERSION] = {
    type: "SingleSelect",
    dataSource: "documents__ListDocumentVersions",
    cutomCell: null,
  };

  const dispatch = useDispatch();

  // if (props.parentSelectedId) {
  //   dispatch(setParentId(props.parentSelectedId));
  // }

  const customRowCellData = useSelector(selectCustomRowData);

  const crudItems = getCrudItems(
    props.nestedListItemProps,
    props.parentSelectedId,
    props.parentSelectedIdName,
    props.lowerCaseParentSelectedIdName
  );

  // Define the url for the table's data endpoint.
  const url = props.nestedListItemProps?.urls?.list ?? endpoints.documents.list;
  const csvUrl =
    props.nestedListItemProps?.urls?.listCsv ?? endpoints.documents.listCSV;

  const redirectUrl = useSelector(selectRedirectUrl);
  const redirectUrlDestinationModule = useSelector(
    selectRedirectUrlDestinationModule
  );

  useEffect(() => {
    if (redirectUrl && redirectUrlDestinationModule === "/documents") {
      setFinalRedirectUrl(redirectUrl);

      dispatch(resetRedirectUrl());
      dispatch(resetRedirectUrlDestinationModule());
    }
  }, [redirectUrl, redirectUrlDestinationModule]);

  return (
    <Category<Data>
      location={location}
      createPermissionsKey={
        props.parentSelectedId ? "documents-create" : "create"
      }
      addBulkPermissionsKey={
        props.parentSelectedId ? "documents-upload" : "upload"
      }
      noun="document"
      idColumn={idColumn}
      listEndpointUrl={finalRedirectUrl ?? url}
      hideCardPage={!props.parentSelectedId}
      cardEndpointData={{
        url: props.parentSelectedId
          ? endpoints.projectmanagement.nestedPages.documents.cardList
          : endpoints.documents.cardList,
        permissionKey: props.parentSelectedId
          ? "documents-card-list"
          : "card-list",
      }}
      defaultBActiveFilterColumnKey={"Documents__bActive"}
      rowFormatter={rowFormatter}
      crudComponents={crudItems}
      hiddenColumns={[]}
      overridePageTitle={props.parentSelectedId ? "Documents" : undefined}
      hideCardPageIcon={true}
      cardProps={{
        cardTitleColumn: ["documents__Name"],
        cardTypeColumn: "documentTypes__DocumentTypesName",
        includeTypeColumn: true,
        cardActionType: props.parentSelectedId ? "documents-view" : "view",
        overwriteCardNoun: "document",
        cardLayout: 6,
        combineTitleAndType: true,
        hideMenuOptions: true,
        imageCardColumn: "documents__FilePath",
        overrideFooterButtonText: "View file",
      }}
      hideHeaderTitle={true}
      listCSV={{ url: csvUrl, noun: "Documents" }}
      rowCellOptions={{
        sites__Id: { width: "Small" },
        sites__SitesName: { width: "Huge" },
        programmes__Name: { width: "SemiLarge" },
        documents__Id: { width: "SemiSmall" },
        documents__bFavourite: {
          width: "Medium2",
        },
        documents__Name: {
          width: "Large",
        },
        documents__DocumentLevel: {
          width: "SemiLarge3",
        },
        documentTypes__DocumentTypesName: {
          width: "Medium",
        },
        documentLevels__DocumentLevelsName: {
          width: "SemiLarge3",
        },
        documents__bActive: { width: "SemiSmall" },

        documents__DocumentType: {
          width: "Huge",
        },
        documents__DocumentFormat: {
          width: "SemiSmall2",
        },
        // documents__DocumentTypeName: {
        //   width: "Huge",
        // },
        documents__DocumentTypeID: {
          width: "Huge",
        },
        documents__DocumentVersion: {
          width: "SemiSmall2",
        },
        documents__AddDate: { width: "SemiLarge" },
        documents__AddedBy: { width: "Medium2" },
        documents__EditDate: { width: "SemiLarge" },
        documents__EditedBy: { width: "SemiLarge2" },
      }}
      headerComp={HeaderDocuComp({
        selectedColumns: [
          "DocumentLevels__DocumentLevelsName",
          // "Documents__DocumentType",
          "DocumentTypes__DocumentTypesName",
          "Documents__AddDate",
        ],
        defaultBActiveFilterColumnKey: props.nestedListItemProps?.urls?.list
          ? undefined
          : "Documents__bActive",
      })}
      customRowCells={customRowCells}
      largeRightPanel={action => action === "CREATE"}
      rowButton={data => <DownloadButton data={data} {...props} />}
      buttonInMiddleTable={false}
      wrapperClassName="DocumentsModule CardListPage"
      // customInitPageContent={CustomInitPage}
      customRowCellColumn={CustomRowCellColumns.DOCUMENTS_VERSION}
      hideRowOptionsMenuComponent={true}
      showIdColumn
      showFilterHeaderButton={true}
      hideFilterTableButton={true}
      showRightTable
      nestedListItemProps={props.nestedListItemProps}
      parentSelectedId={props.parentSelectedId}
    />
  );
};

export default Documents;

const DownloadButton = (props: Props & { data: any }) => {
  const { data } = props;
  const dispatch = useDispatch();

  const customRowCellData = useSelector(selectCustomRowData);

  const submitResponseErrors = useSelector(selectSubmitResponse)
    .submitResponseErrors;
  const hasMounted = useRef<boolean>(false);

  useEffect(() => {
    if (submitResponseErrors && hasMounted.current) {
      toast(
        <ToastMessage
          toastData={Object.entries(submitResponseErrors).map(
            ([title, text]: [string, any]) => ({
              title,
              text,
            })
          )}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }
    if (!hasMounted.current) {
      dispatch(clearSubmitResponseErrors());
      hasMounted.current = true;
    }
  }, [submitResponseErrors]);

  return (
    <div
      className="DownloadBtn"
      onClick={event => {
        event.stopPropagation();
        documentsDownloadSingleDoc(
          data,
          customRowCellData,
          CustomRowCellColumns.DOCUMENTS_VERSION,
          "post",
          dispatch,
          props.nestedListItemProps?.urls?.download ?? undefined,
          props.lowerCaseParentSelectedIdName ?? undefined,
          // props.parentSelectedIdName ?? undefined,
          props.parentSelectedId,
          props.parentSelectedId
            ? "documents__FileExtension"
            : "documents__DocumentFormat"
        );
      }}
    >
      <img src={download_png} />
    </div>
  );
};
